import apiUrl from '@/apis/api';
import { postWithAuth } from '@/lib/httpAdapter';

/**
 * Reads GitHub data through the backend instead of calling api.github.com from
 * the browser. The backend runs the call on the PR-bot installation token for
 * the repository, so the signed-in user's GitHub token is never needed here.
 *
 * The backend restricts this to repos the caller's org has the bot installed on
 * and to an allowlist of paths, so it is not a general GitHub proxy.
 */
export type GithubReadArgs = {
  /** `owner/repo` to read from. */
  repoName: string;
  /** Allowlisted suffix, e.g. `/pulls/12/files`. Defaults to the repo itself. */
  path?: string;
  method?: 'GET' | 'POST';
  query?: Record<string, string | number | boolean | undefined>;
  body?: Record<string, unknown>;
};

export class GithubReadError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'GithubReadError';
    this.status = status;
    this.code = code;
  }
}

export async function githubRead<T = unknown>({
  repoName,
  path = '',
  method = 'GET',
  query,
  body,
}: GithubReadArgs): Promise<T> {
  const cleanQuery = Object.fromEntries(
    Object.entries(query ?? {})
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)])
  );

  const response = await postWithAuth(`${apiUrl}githubRead/`, {
    RepoName: repoName,
    Path: path,
    Method: method,
    Query: cleanQuery,
    Body: body,
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success === false) {
    throw new GithubReadError(
      payload?.error ?? 'We could not read that from GitHub.',
      payload?.status ?? response.status,
      payload?.code ?? 'internal_error'
    );
  }

  return payload?.data as T;
}

export type ListInstalledReposArgs = {
  /** 1-based page number. */
  page?: number;
  /** Page size. GitHub caps this at 100. */
  perPage?: number;
  /** Case-insensitive substring filter on the repository name. */
  search?: string;
};

export type ListInstalledReposResult<T = unknown> = {
  repositories: T[];
  total_count: number;
  has_more: boolean;
  page: number;
  per_page: number;
};

/**
 * Lists the GitHub repositories the PR bot is installed on for the caller's org.
 *
 * Replaces the browser's `GET /orgs/{org}/repos` call, which needed the
 * signed-in user's `repo` scope. The backend runs the listing on the org's
 * installation token, so the login OAuth grant can stay least-privilege. The
 * org comes from the caller's JWT, and filtering happens server-side because
 * the installation endpoint has no search parameter of its own.
 *
 * Repositories are returned as raw GitHub repo objects, so existing frontend
 * normalisation keeps working.
 */
export async function listInstalledRepos<T = unknown>({
  page = 1,
  perPage = 100,
  search,
}: ListInstalledReposArgs = {}): Promise<ListInstalledReposResult<T>> {
  const response = await postWithAuth(`${apiUrl}githubInstalledRepos/`, {
    Page: page,
    PerPage: perPage,
    Search: search,
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success === false) {
    throw new GithubReadError(
      payload?.error ?? 'We could not load your repositories from GitHub.',
      payload?.status ?? response.status,
      payload?.code ?? 'internal_error'
    );
  }

  const repositories: T[] = Array.isArray(payload?.repositories)
    ? payload.repositories
    : [];

  return {
    repositories,
    total_count:
      typeof payload?.total_count === 'number'
        ? payload.total_count
        : repositories.length,
    has_more: Boolean(payload?.has_more),
    page: typeof payload?.page === 'number' ? payload.page : page,
    per_page:
      typeof payload?.per_page === 'number' ? payload.per_page : perPage,
  };
}

/** The organization-scoped reads the backend exposes. */
/** `user_orgs` is not here on purpose: the caller's own membership stays a
 * direct browser read (`githubUserOrgs.ts`), because it runs before a workspace
 * or a stored token exists. */
export type GithubOrgReadOp = 'org_members' | 'org_teams' | 'team_members';

export type GithubOrgReadArgs = {
  op: GithubOrgReadOp;
  /** GitHub org login. Required for `org_members` and `org_teams`. */
  org?: string;
  /** Required for `team_members`, alongside `org`. */
  teamSlug?: string;
  /** 1-based page number. */
  page?: number;
  /** Page size. GitHub caps this at 100. */
  perPage?: number;
};

export type GithubOrgReadResult<T = unknown> = {
  /** Untouched GitHub payload: an array or an object, depending on the op. */
  data: T;
  /** Derived from GitHub's Link header, so paging loops can drive off it. */
  has_more: boolean;
  page: number;
  per_page: number;
};

/**
 * Reads GitHub organization, team and user data through the backend.
 *
 * Replaces the browser's own `/user/orgs`, `/orgs/{org}/members`,
 * `/orgs/{org}/teams` and `/teams/{id}/members` calls. Each of those needed the
 * signed-in user's GitHub token to be present in the page; the backend resolves
 * the credential itself, so the login OAuth grant can stay least-privilege and
 * the frontend holds no GitHub credential at all.
 *
 * @throws GithubReadError carrying the backend envelope's status and code.
 */
export async function githubOrgRead<T = unknown>({
  op,
  org,
  teamSlug,
  page,
  perPage,
}: GithubOrgReadArgs): Promise<GithubOrgReadResult<T>> {
  const response = await postWithAuth(`${apiUrl}githubOrgRead/`, {
    Op: op,
    Org: org,
    TeamSlug: teamSlug,
    Page: page,
    PerPage: perPage,
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success === false) {
    throw new GithubReadError(
      payload?.error ?? 'We could not read that from GitHub.',
      payload?.status ?? response.status,
      payload?.code ?? 'internal_error'
    );
  }

  return {
    data: payload?.data as T,
    has_more: Boolean(payload?.has_more),
    page: typeof payload?.page === 'number' ? payload.page : (page ?? 1),
    per_page:
      typeof payload?.per_page === 'number' ? payload.per_page : (perPage ?? 0),
  };
}

/**
 * Searches GitHub issues and pull requests through the backend instead of
 * calling api.github.com from the browser. The backend runs the search on the
 * PR-bot installation token, so the signed-in user's login grant can stay
 * least-privilege and no longer needs the `repo` scope.
 *
 * The GitHub `q` string is built server-side from these fields, and the org /
 * repo scoping is injected there too: there is deliberately no way to pass a
 * raw query, so this is not a general GitHub search proxy.
 */
